import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { InsurancePayer, InsurancePayerControllerApi } from "@/services";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getXtenantId,
  useBaseContent,
  useMutationWithLoading,
} from "@/utils/utils";
import {
  PAYER_NAME_LOOKUP_PAGE_SIZE,
  PAYER_QUERY_KEY,
  PayerRow,
  PayerUsage,
} from "../payers-constant";
import { toPayerUsage } from "../payers-utils";

export interface PayerListParams {
  pagination: DGT_Pagination;
  sortBy?: string;
  sortDirection?: string;
  searchString?: string;
  active?: boolean;
  payerType?: InsurancePayer["payerType"];
  isArchived: boolean;
}

interface UpdatePayerParams {
  currentPayerId: string;
  payer: InsurancePayer;
}

interface PayerStatusParams {
  payerId: string;
  status: boolean;
}

const getPayerApi = () => new InsurancePayerControllerApi(getConfigObject());

const useInvalidatePayers = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      Object.values(PAYER_QUERY_KEY).map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      )
    );
};

const fetchPayers = (
  {
    pagination,
    sortBy,
    sortDirection,
    searchString,
    active,
    payerType,
    isArchived,
  }: PayerListParams,
  tenantId?: string
) =>
  getPayerApi()
    .getInsurancePayersByStatus(
      pagination.page,
      pagination.pageSize,
      sortBy,
      sortDirection,
      searchString || undefined,
      active,
      isArchived,
      payerType,
      tenantId
    )
    .then((res) => asPaginatedContent<PayerRow>(res?.data));

export const usePayerList = (
  params: PayerListParams
): useBaseContent<PayerRow> => {
  const tenantId = getXtenantId();

  return useQuery({
    queryKey: [PAYER_QUERY_KEY.LIST, tenantId, params],
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: () => fetchPayers(params, tenantId),
  });
};

export const usePayerTotalCount = (isArchived: boolean, enabled: boolean) => {
  const tenantId = getXtenantId();

  return useQuery({
    queryKey: [PAYER_QUERY_KEY.TOTAL, tenantId, isArchived],
    enabled,
    refetchOnWindowFocus: false,
    queryFn: () =>
      fetchPayers(
        { pagination: { page: 0, pageSize: 1 }, isArchived },
        tenantId
      ),
    select: (res) => res?.data?.page?.totalElements ?? 0,
  });
};

export const usePayerUsageLookup = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (payerId: string): Promise<PayerUsage> =>
      queryClient.fetchQuery({
        queryKey: [PAYER_QUERY_KEY.USAGE, getXtenantId(), payerId],
        staleTime: 0,
        queryFn: () =>
          getPayerApi()
            .getInsurancePayerUsage(payerId, getXtenantId())
            .then((res) => toPayerUsage(res?.data?.data)),
      }),
    [queryClient]
  );
};

export const usePayerNameExists = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (payerName: string, excludePayerId?: string) => {
      const tenantId = getXtenantId();
      const target = payerName.trim().toLowerCase();
      const res = await queryClient.fetchQuery({
        queryKey: [PAYER_QUERY_KEY.NAME_LOOKUP, tenantId, target],
        staleTime: 0,
        queryFn: () =>
          fetchPayers(
            {
              pagination: { page: 0, pageSize: PAYER_NAME_LOOKUP_PAGE_SIZE },
              searchString: payerName.trim(),
              isArchived: false,
            },
            tenantId
          ),
      });
      return (res?.data?.content ?? []).some(
        (payer) =>
          payer.payerId !== excludePayerId &&
          payer.payerName?.trim().toLowerCase() === target
      );
    },
    [queryClient]
  );
};

export const usePayerIdExists = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (payerId: string) => {
      const tenantId = getXtenantId();
      const target = payerId.trim().toLowerCase();
      const [active, archived] = await Promise.all(
        [false, true].map((isArchived) =>
          queryClient.fetchQuery({
            queryKey: [PAYER_QUERY_KEY.ID_LOOKUP, tenantId, target, isArchived],
            staleTime: 0,
            queryFn: () =>
              fetchPayers(
                {
                  pagination: {
                    page: 0,
                    pageSize: PAYER_NAME_LOOKUP_PAGE_SIZE,
                  },
                  searchString: payerId.trim(),
                  isArchived,
                },
                tenantId
              ),
          })
        )
      );
      return [active, archived].some((res) =>
        (res?.data?.content ?? []).some(
          (payer) => payer.payerId?.trim().toLowerCase() === target
        )
      );
    },
    [queryClient]
  );
};

export const useCreatePayer = () => {
  const invalidate = useInvalidatePayers();
  return useMutationWithLoading(
    async (payer: InsurancePayer) =>
      getPayerApi()
        .addInsurancePayer(payer, getXtenantId())
        .then((res) => res?.data),
    { onSuccess: invalidate }
  );
};

export const useUpdatePayer = () => {
  const invalidate = useInvalidatePayers();
  return useMutationWithLoading(
    async ({ currentPayerId, payer }: UpdatePayerParams) =>
      getPayerApi()
        .updateInsurancePayer(currentPayerId, payer, getXtenantId())
        .then((res) => res?.data),
    { onSuccess: invalidate }
  );
};

export const useUpdatePayerStatus = () => {
  const invalidate = useInvalidatePayers();
  return useMutationWithLoading(
    async ({ payerId, status }: PayerStatusParams) =>
      getPayerApi()
        .updateInsurancePayerStatus(payerId, status, getXtenantId())
        .then((res) => res?.data),
    { onSuccess: invalidate }
  );
};

export const useArchivePayer = () => {
  const invalidate = useInvalidatePayers();
  return useMutationWithLoading(
    async ({ payerId, status }: PayerStatusParams) =>
      getPayerApi()
        .updateInsurancePayerArchiveStatus(payerId, status, getXtenantId())
        .then((res) => res?.data),
    { onSuccess: invalidate }
  );
};
